import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { database } from '@/firebase';
import { TEXTDATEFORMAT } from '@/utilities/time';
import sortArray from '@/utilities/sortArray';

dayjs.extend(isBetween);

const getUser = ({ user, payments, site }) => {
  if (user) {
    const isUserPayment = payment => payment.email === user.email;
    const userPayments = payments && payments.filter(isUserPayment);
    const admin = site && site.find(record => record.id === 'admin');
    const isAdmin = admin && admin.users.includes(user.email);
    return {
      ...user,
      payments: userPayments,
      isAdmin,
    };
  }
  return null;
};

const findStripWithNumber = (strips, stripNumber) => (
  strips.find(strip => strip && (strip.number === parseInt(stripNumber, 10)))
);

const findStripWithDate = (strips, publishDate) => {
  const isSameDate = (unixDate, stringDate) => (
    dayjs.unix(unixDate).isSame(dayjs(stringDate), 'day')
  );
  return strips.find(strip => strip && isSameDate(strip.publishDate.seconds, publishDate));
};

const findStrip = ({ state, stripNumber, publishDate }) => {
  const { strips } = state;
  if (strips) {
    if (stripNumber) {
      return findStripWithNumber(strips, stripNumber);
    }
    if (publishDate) {
      return findStripWithDate(strips, publishDate);
    }
  }
  return null;
};

const formatSeason = (season) => {
  const { startDate, endDate } = season;
  const startsAt = dayjs.unix(startDate.seconds).format(TEXTDATEFORMAT);
  const endsAt = dayjs.unix(endDate.seconds).format(TEXTDATEFORMAT);
  return {
    ...season,
    startsAt,
    endsAt,
  };
};

const getStripSeason = ({ state, stripNumber }) => {
  const strip = findStrip({ state, stripNumber });
  const { seasons } = state;
  if (strip && seasons) {
    const stripSeason = seasons.find((season) => {
      const stripDate = dayjs.unix(strip.publishDate.seconds);
      const seasonStart = dayjs.unix(season.startDate.seconds);
      const seasonEnd = dayjs.unix(season.endDate.seconds);
      return stripDate.isBetween(seasonStart, seasonEnd);
    });
    return stripSeason && formatSeason(stripSeason);
  }
  return null;
};

const hasPaidForSeason = ({ season, user }) => {
  if (user.isAdmin) return true;
  return user.payments
    && user.payments.find(payment => payment.season.toLowerCase() === season.name.toLowerCase());
};

const isPublished = (strip) => {
  const { publishDate } = strip;
  const { getDBTime } = database;
  const dbTime = dayjs.unix(getDBTime().seconds);
  const publishDayjs = dayjs.unix(publishDate.seconds);
  return dbTime.isAfter(publishDayjs);
};

const formatStrip = ({ state, strip }) => {
  const user = getUser(state);
  const season = getStripSeason({ state, stripNumber: strip.number });
  const publishDayjs = dayjs.unix(strip.publishDate.seconds);
  const userCanRead = season && user && hasPaidForSeason({ season, user });
  return {
    ...strip,
    season,
    publishDay: publishDayjs.format(TEXTDATEFORMAT),
    isPublished: isPublished(strip),
    userCanRead: strip.free || userCanRead,
  };
};

const getStrip = ({ state, stripNumber, options }) => {
  const strip = findStrip({ state, stripNumber });
  if (strip) {
    if (options && options.isPublished && !isPublished(strip)) {
      return null;
    }
    return formatStrip({ state, strip });
  }
  return null;
};

const getStripByDate = ({ state, publishDate }) => {
  const strip = findStrip({ state, publishDate });
  return strip ? formatStrip({ state, strip }) : null;
};

const getAllStrips = ({ state, options }) => {
  let strips = state.strips && state.strips.map(strip => formatStrip({ state, strip }));
  if (strips && options && options.isPublished) {
    strips = strips.filter(strip => isPublished(strip));
  }
  return strips && sortArray({
    data: strips,
    prop: 'number',
    sort: options && options.sort,
  });
};

export default {
  getLatestStripNumber: state => state.latestStrip,
  getStrip: state => (stripNumber, options) => getStrip({ state, stripNumber, options }),
  getStripByDate: state => publishDate => getStripByDate({ state, publishDate }),
  getAllStrips: state => options => getAllStrips({ state, options }),
  getLastRead: state => state.lastRead,
};
